import React, { useEffect, useRef } from 'react';
import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import HorizontalSlider from '@smart-tv/components/horizontal-slider/horizontal-slider';
import useVodPlayer from '@smart-tv/components/player/vod-wrapper/_hooks/use-vod-player';
import { StreamOptions } from '@smart-tv/components/player/vod-wrapper/_contexts/stream-player-context';
import usePlayerEvent from '@smart-tv/components/player/_hooks/use-player-event';
import { PlayerEvent, SeekableRange } from '@smart-tv/components/player/types';
import NpvrCard, { NpvrCardProps } from './npvr-card';
import styles from './npvr.module.scss';
import { Typography } from '../typography';

export type NpvrSliderItem = Omit<NpvrCardProps, 'listRef' | 'index'> & { id: string };

interface Props {
  items: NpvrSliderItem[];
  displayString: string;
  assetPressHandler?: () => void;
}

// Renders cards (and their lazy-loaded images) in batches instead of all at once, growing
// as the end of the list scrolls into view - recording lists can otherwise get long.
const BATCH_SIZE = 15;

const streamKey = (sid?: number, eventId?: number) => `${sid}-${eventId}`;

const getProgress = (durationInSeconds: number | undefined, savedStreamOptions?: StreamOptions) => {
  if (
    !durationInSeconds ||
    durationInSeconds <= 0 ||
    !(savedStreamOptions?.streamParams.progress !== undefined)
  ) {
    return undefined;
  }
  return Math.min(100, (savedStreamOptions.streamParams.progress / durationInSeconds) * 100);
};

// Reuses HorizontalSlider (program-sheet's episode selector list uses the same component)
// across the Optaget/Udløber/Planlagt/Fejl tabs.
export const NpvrSlider: React.FC<Props> = ({ items, displayString, assetPressHandler }) => {
  const { ref: listRef, focusKey } = useFocusable({
    trackChildren: true,
    saveLastFocusedChild: true
  });
  const [streamOptionsMap, setStreamOptionsMap] = React.useState<Record<string, StreamOptions>>({});

  const { isPlaying, streamOptions } = useVodPlayer();
  const wasPlayingRef = useRef(isPlaying);
  // streamOptions holds the params the stream was opened with, so track the live position here.
  const lastProgressRef = useRef<number>();

  usePlayerEvent(PlayerEvent.PlayTimeUpdated, (seekableRange: SeekableRange) => {
    if (!seekableRange) return;
    lastProgressRef.current = Math.floor(seekableRange.position - seekableRange.range.start);
  });

  useEffect(() => {
    if (!wasPlayingRef.current && isPlaying) {
      lastProgressRef.current = undefined;
    }
    if (wasPlayingRef.current && !isPlaying && streamOptions) {
      const { sid, eventId } = streamOptions.streamParams;
      const updatedOptions: StreamOptions = {
        ...streamOptions,
        streamParams: {
          ...streamOptions.streamParams,
          progress: lastProgressRef.current ?? streamOptions.streamParams.progress
        }
      };
      setStreamOptionsMap((prev) => ({ ...prev, [streamKey(sid, eventId)]: updatedOptions }));
    }
    wasPlayingRef.current = isPlaying;
  }, [isPlaying, streamOptions]);

  const navigationStateKey = items.map((item) => item.id).join('|');

  const [visibleCount, setVisibleCount] = React.useState(BATCH_SIZE);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Preserve loaded batches across list edits (e.g. deleting a card) - resetting on every
  // navigationStateKey change would unmount neighboring cards mid-interaction, disrupting
  // remote focus restoration and clamping scroll. Switching tabs already remounts this
  // slider as a new component instance, which starts fresh on its own.

  React.useEffect(() => {
    const listElement = listRef.current;
    const sentinel = sentinelRef.current;
    if (!listElement || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((current) => Math.min(items.length, current + BATCH_SIZE));
        }
      },
      { root: listElement, rootMargin: '0px 600px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [listRef, items.length, visibleCount]);

  const visibleItems = items.slice(0, visibleCount);

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['npvr__event-box']}>
        <Typography textAlign="center" variant="body">
          {displayString}
        </Typography>
        {items.length > 0 && (
          <HorizontalSlider
            listRef={listRef}
            navigationStateKey={`${navigationStateKey}|${visibleCount}`}
            className={styles['npvr-slider']}
            listClassName={styles['npvr-slider__list']}
            contentClassName={styles['npvr-slider__content']}
            navClassName={styles['npvr-slider__nav']}
          >
            {visibleItems.map(({ id, ...item }, index) => {
              const params = item.streamOptions?.streamParams;
              const saved = params && streamOptionsMap[streamKey(params.sid, params.eventId)];
              return (
                <NpvrCard
                  key={`${id}-${index}`}
                  {...item}
                  streamOptions={saved ?? item.streamOptions}
                  progress={getProgress(item.durationInSeconds, saved) ?? item.progress}
                  listRef={listRef}
                  index={index}
                  assetPressHandler={assetPressHandler}
                />
              );
            })}
            {visibleCount < items.length && (
              <div ref={sentinelRef} className={styles['npvr-slider__sentinel']} />
            )}
          </HorizontalSlider>
        )}
      </div>
    </FocusContext.Provider>
  );
};

export default NpvrSlider;
